import React, { useEffect, useState } from 'react';
import { migratedLinks, toggleFailureMessage } from '../lib/pluginSchedule.mjs';

// AUTOMATIONS — ported from views/integrations/automations.js (2026-08-24).
// What runs, and what happened when it ran — one page, because they are one
// question asked two ways. An automation is plural per plugin; the card keeps
// the connection, this keeps everything that runs against it.
//
// PLUGIN SCHEDULES ARE PARADIGM'S. This page used to create and edit them (a
// run_plugin automation carrying plugin_config), which made this app a second
// place plugin configuration lived. Paradigm owns that now and is migrating the
// automations made here; this page lists them, switches them and removes them,
// and sends you to Paradigm (via each plugin's card) to change one.
//
// The cron helpers (cronInWords / pickerForCron / _browserTz) stay in the
// legacy cron.js classic script — plugins.js still consumes them — so this
// view reads them off window like every other engine global.

const AUTO_STYLE = `
  .at-wrap { padding: 26px 30px 80px; max-width: 980px; margin: 0 auto; }
  .at-head { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; }
  .at-title { font-family: var(--font-serif); font-size: 26px; font-weight: 300; color: var(--ink); }
  .at-note { font-size: 12.5px; color: var(--ink3); }
  .at-note a { color: var(--ink2); }

  .at-list { margin-top: 22px; border-top: 1px solid var(--border-lt); }
  .at-row {
    display: grid; grid-template-columns: 1fr auto auto; gap: 14px; align-items: center;
    padding: 13px 6px; border-bottom: 1px solid var(--border-lt);
  }
  .at-row-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; cursor: pointer; }
  .at-name { font-size: 14.5px; color: var(--ink); display: flex; align-items: center; gap: 8px; }
  .at-plugin {
    font-family: var(--font-mono); font-size: 10px; color: var(--ink3);
    background: var(--dark3); padding: 1px 6px; border-radius: 3px;
  }
  .at-when { font-size: 12.5px; color: var(--ink3); }
  /* Moved onto the plugin's own schedules by Paradigm: says so, and links there,
     in place of an off switch that could not be turned on. */
  .at-moved { font-size: 12.5px; color: var(--ink2); }
  .at-moved a { color: var(--ink2); margin-left: 8px; }
  .at-moved-gap { width: 1px; }
  .at-when code {
    font-family: var(--font-mono); font-size: 10.5px; color: var(--ink4);
    background: var(--dark3); padding: 1px 5px; border-radius: 3px; margin-left: 6px;
  }
  .at-last { font-size: 12.5px; text-align: right; }
  .at-last.failed { color: var(--terra); }
  .at-last.awaiting_approval { color: var(--terra-lt); }
  .at-last.nothing_new, .at-last.running { color: var(--ink4); }
  .at-last.wrote { color: var(--moss-mid, var(--accent)); }
  .at-never { color: var(--ink4); font-style: italic; }

  .at-runs { padding: 4px 6px 16px; background: var(--dark2); border-bottom: 1px solid var(--border-lt); }
  .at-runs-head {
    font-family: var(--font-mono); font-size: 10.5px; text-transform: uppercase;
    letter-spacing: 0.14em; color: var(--ink3); padding: 10px 0 6px;
  }
  .at-run {
    display: grid; grid-template-columns: auto 84px 1fr auto; gap: 14px; align-items: baseline;
    padding: 7px 0; border-bottom: 1px solid var(--border-lt); width: 100%;
    background: none; border-left: 0; border-right: 0; border-top: 0;
    text-align: left; font: inherit; color: var(--ink2); cursor: pointer;
  }
  .at-run:hover { background: var(--dark3); }
  .at-run-when {
    font-family: var(--font-mono); font-size: 11px; color: var(--ink4);
    white-space: nowrap; min-width: 190px;
  }
  .at-tag {
    font-family: var(--font-mono); font-size: 9.5px; text-transform: uppercase;
    letter-spacing: 0.1em; padding: 2px 7px; border-radius: 3px; white-space: nowrap;
    justify-self: start;
  }
  .at-tag.wrote { background: var(--moss-pale); color: var(--moss); }
  .at-tag.failed { background: rgba(176,78,40,0.13); color: var(--terra); }
  .at-tag.awaiting_approval { background: rgba(176,78,40,0.09); color: var(--terra-lt); }
  .at-tag.nothing_new { background: var(--dark3); color: var(--ink4); }
  .at-tag.running { background: var(--dark3); color: var(--ink3); }
  .at-detail-text { color: var(--ink2); font-size: 12.5px; }

  .at-filters { display: flex; gap: 7px; flex-wrap: wrap; margin: 18px 0 0; }
  .at-filter {
    font-family: var(--font-mono); font-size: 10.5px; letter-spacing: 0.05em;
    background: none; border: 1px solid var(--border); border-radius: 3px;
    color: var(--ink3); padding: 4px 9px; cursor: pointer;
  }
  .at-filter.on { color: var(--ink); border-color: var(--ink3); background: var(--dark2); }
  .at-filter-count { color: var(--ink4); margin-left: 5px; }
  .at-run-dur { font-family: var(--font-mono); font-size: 11px; color: var(--ink4); }
  /* Destructive and rare, so it reads as neither a primary action nor a
     decoration: quiet until pointed at, and never coloured — the palette has no
     danger token and inventing one here puts a hex outside the system. */
  .at-delete {
    font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.04em;
    color: var(--ink4); background: none; border: 0; cursor: pointer;
    padding: 2px 6px; margin-left: 2px; opacity: 0;
    transition: opacity 120ms ease, color 120ms ease;
  }
  .at-row:hover .at-delete, .at-delete:focus-visible { opacity: 1; }
  .at-delete:hover { color: var(--ink); text-decoration: underline; }
  .at-delete[disabled] { opacity: 1; color: var(--ink4); cursor: default; text-decoration: none; }
  .at-nodes { padding: 6px 0 10px 26px; }
  .at-node { display: flex; gap: 9px; padding: 3px 0; font-size: 12.5px; align-items: baseline; }
  .at-node-schema {
    font-family: var(--font-mono); font-size: 10px; color: var(--ink4);
    background: var(--dark3); padding: 1px 5px; border-radius: 3px; flex: none;
  }
  .at-err {
    font-family: var(--font-mono); font-size: 11.5px; color: var(--terra);
    background: rgba(176,78,40,0.09); padding: 7px 9px; border-radius: 3px;
    white-space: pre-wrap; word-break: break-word; margin: 6px 0;
  }

  .at-empty { padding: 46px 6px; text-align: center; }
  /* A FRESH SLATE: no automations at all — the page is the one verb, centred
     in the content block, flush: no fill, no outline, no heading around it. */
  .at-fresh { height: 100%; display: grid; place-items: center; }
  .at-fresh-add {
    background: none; border: 0; padding: 12px 16px; cursor: pointer; text-decoration: none;
    font-family: var(--ofself-font-mono, ui-monospace, monospace); font-size: 12px;
    letter-spacing: 0.16em; text-transform: uppercase; color: var(--shell-ink, var(--ink));
    opacity: 0.7; transition: opacity 0.15s ease;
  }
  .at-fresh-add:hover, .at-fresh-add:focus-visible { opacity: 1; }
  .at-empty-title { font-family: var(--font-serif); font-size: 16px; color: var(--ink2); }
  .at-empty-body { font-size: 13px; color: var(--ink4); margin-top: 5px; }
`;

const api = {
  get: (p) => window.igApi.get(p),
  post: (p, b) => window.igApi.post(p, b),
  put: (p, b) => window.igApi.put(p, b),
  del: (p) => window.igApi.del(p),
};

// The tag is the scannable half; the sentence beside it is the detail.
const TAG = {
  wrote: 'ok', nothing_new: 'no change', failed: 'failed',
  awaiting_approval: 'needs you', running: 'running',
};
const Tag = ({ kind }) => (kind
  ? <span className={`at-tag ${kind}`}>{TAG[kind] || kind}</span> : null);

// Both clocks. The distance between them is what made a four-hour scheduling
// error invisible for a week.
function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const local = d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  return `${local} · ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}Z`;
}

// The detail carries what the tag cannot; repeating the tag's word buries the
// only part that identifies the problem.
function detailOf(r) {
  const kind = r.outcome?.kind;
  const label = r.outcome?.label || r.status || '';
  if (kind === 'failed') {
    return label
      .replace(/^failed\s+—\s+/, '')
      .replace(/^plugin execution failed:\s*/i, '');
  }
  if (kind === 'wrote') return label.replace(/^wrote\s+/, '');
  return '';
}

function scheduleWords(a) {
  const cfg = a.config || {};
  if ((a.trigger_type || 'schedule') !== 'schedule') return a.trigger_type === 'event' ? 'when another finishes' : 'on demand';
  if (!cfg.cron) return `every ${cfg.interval_minutes || 30} min`;
  return `${window.cronInWords(cfg.cron)} · ${cfg.timezone || 'UTC'}`;
}

// Worst first, and a fixed order stops the chips reshuffling as counts change.
const KIND_ORDER = ['failed', 'awaiting_approval', 'running', 'wrote', 'nothing_new'];

export default function Automations({ active }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [runNodes, setRunNodes] = useState({});
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setErr(null);
    Promise.all([
      api.get('/process/automations/status'),
      api.get('/process/runs/ledger?limit=200').catch(() => ({ runs: [] })),
      api.get('/api/plugins').catch(() => ({ plugins: [] })),
    ]).then(([status, ledger, pl]) => {
      if (!live) return;
      setData({
        automations: status.automations || [],
        runs: ledger.runs || [],
        plugins: pl.plugins || [],
      });
    }).catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [active]);

  if (!active) return null;
  if (err) return <div style={{ padding: 48, color: 'var(--terra)', fontStyle: 'italic' }}>could not load automations: {err}</div>;
  if (!data) return <div className="loading"><div className="spinner" /></div>;

  const { automations, runs, plugins } = data;
  const runsFor = (a) => runs.filter((r) => r.automation_id === a.paradigm_id);
  // The runs the current filter admits — the same predicate that counted the
  // chip, so "failed 3" and the rows on screen are one set.
  const runsMatching = (a) => {
    const mine = runsFor(a);
    return filter ? mine.filter((r) => r.outcome?.kind === filter) : mine;
  };
  const shown = filter ? automations.filter((a) => runsMatching(a).length) : automations;

  const counts = {};
  for (const r of runs) {
    const k = r.outcome?.kind;
    if (k) counts[k] = (counts[k] || 0) + 1;
  }
  const kinds = KIND_ORDER.filter((k) => counts[k])
    .concat(Object.keys(counts).filter((k) => !KIND_ORDER.includes(k)));

  const pickFilter = (k) => {
    const next = (!k || filter === k) ? null : k;
    setFilter(next);
    const matches = next ? automations.filter((a) => runsFor(a).some((r) => r.outcome?.kind === next)) : automations;
    setOpen(next && matches.length === 1 ? matches[0].paradigm_id : null);
    setOpenRun(null);
  };

  const toggleRun = async (r) => {
    if (openRun === r.id) { setOpenRun(null); return; }
    setOpenRun(r.id);
    if (!runNodes[r.id]) {
      let d;
      try {
        d = await api.get(`/process/runs/ledger/${encodeURIComponent(r.automation_id)}/${encodeURIComponent(r.id)}`);
      } catch (e) { d = { error: e.message }; }
      setRunNodes((m) => ({ ...m, [r.id]: d }));
    }
  };

  const toggleEnabled = async (a, checked, input) => {
    try {
      await api.post(`/process/automations/${encodeURIComponent(a.paradigm_id)}/active`, { enabled: checked });
    } catch (e) {
      input.checked = !checked;
      window.showToast?.(toggleFailureMessage(e), 'error');
      // Paradigm moved it while this page was open: show the row as moved,
      // with its links, rather than leave a switch that will keep refusing.
      if (e.body?.error === 'MIGRATED') {
        setData((d) => ({
          ...d,
          automations: d.automations.map((x) => (x.paradigm_id === a.paradigm_id
            ? { ...x, enabled: false, migrated: true, moved_to: e.body.moved_to || x.moved_to || [] } : x)),
        }));
      }
    }
  };

  const remove = async (a, btn) => {
    const name = a.name || 'this automation';
    // Confirmed because it is not undoable and the row it removes is the only
    // record of the schedule — there is no trash to fish it back out of.
    if (!window.confirm(`Delete "${name}"? Its schedule and run history go with it. This cannot be undone.`)) return;
    btn.disabled = true;
    btn.textContent = 'removing…';
    try {
      await api.del(`/process/automations/manage/${encodeURIComponent(a.paradigm_id)}`);
      setData((d) => ({ ...d, automations: d.automations.filter((x) => x.paradigm_id !== a.paradigm_id) }));
      setOpen((o) => (o === a.paradigm_id ? null : o));
      window.showToast?.(`removed ${name}`, 'success');
    } catch (e2) {
      btn.disabled = false;
      btn.textContent = 'remove';
      window.showToast?.('could not remove that: ' + e2.message, 'error');
    }
  };

  const RunNodes = ({ r }) => {
    const d = runNodes[r.id];
    if (!d) return <div className="at-nodes"><span className="at-node-schema">loading…</span></div>;
    if (d.error) return <div className="at-nodes"><div className="at-err">{d.error}</div></div>;
    const nodeErr = d.run?.error_message
      || ((d.run?.output_summary?.steps || []).find((s) => s && s.error) || {}).error;
    const nodes = d.nodes || [];
    return (
      <div className="at-nodes">
        {nodeErr ? <div className="at-err">{nodeErr}</div> : null}
        {nodes.map((n) => (
          <div className="at-node" key={n.id}>
            <span className="at-node-schema">{n.schema_name || 'node'}</span>
            <span>{n.title || n.id}</span>
          </div>
        ))}
        {!nodes.length && !nodeErr ? <div className="at-node"><span>wrote no nodes</span></div> : null}
        {d.total > nodes.length ? <div className="at-node"><span>showing {nodes.length} of {d.total}</span></div> : null}
      </div>
    );
  };

  const RunList = ({ a }) => {
    const mine = runsMatching(a);
    if (!mine.length) {
      return <div className="at-runs"><div className="at-runs-head">no runs yet</div></div>;
    }
    return (
      <div className="at-runs">
        <div className="at-runs-head">runs{filter ? ` · ${TAG[filter] || filter} only` : ''}</div>
        {mine.slice(0, 20).map((r) => (
          <React.Fragment key={r.id}>
            <button type="button" className="at-run" onClick={() => toggleRun(r)}>
              <span className="at-run-when">{when(r.started_at)}</span>
              <Tag kind={r.outcome?.kind} />
              <span className="at-detail-text">{detailOf(r)}</span>
              <span className="at-run-dur">{r.duration_s === null ? '' : `${r.duration_s}s`}</span>
            </button>
            {openRun === r.id ? <RunNodes r={r} /> : null}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const Row = ({ a }) => {
    const last = runsFor(a)[0];
    const moved = migratedLinks(a, plugins, window.location.href);
    return (
      <>
        <div className="at-row">
          <span className="at-row-main" onClick={() => { setOpen(open === a.paradigm_id ? null : a.paradigm_id); setOpenRun(null); }}>
            <span className="at-name">{a.name}
              {(a.steps || []).filter((s) => s.plugin_id).map((s, i) => {
                const p = plugins.find((x) => x.id === s.plugin_id);
                return <span className="at-plugin" key={i}>{p ? p.name : 'plugin'}</span>;
              })}
            </span>
            <span className="at-when">{scheduleWords(a)}
              {(a.config || {}).cron ? <code>{a.config.cron}</code> : null}
            </span>
            {moved ? (
              <span className="at-moved">{moved.text}
                {moved.links.map((l) => (
                  <a key={l.plugin_id} href={l.href} onClick={(e) => e.stopPropagation()}>{l.name} →</a>
                ))}
              </span>
            ) : null}
          </span>
          <span className={`at-last ${last?.outcome?.kind || ''}`}>
            {last
              ? <><Tag kind={last.outcome?.kind} /> <span className="at-detail-text">{detailOf(last)}</span></>
              : <span className="at-never">never run</span>}
          </span>
          {moved ? <span className="at-moved-gap" /> : (
            <label className="automation-toggle" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" defaultChecked={!!a.enabled}
                     onChange={(e) => toggleEnabled(a, e.target.checked, e.target)} />
              <span className="automation-toggle-track" />
            </label>
          )}
          <button type="button" className="at-delete" title="delete this automation"
                  onClick={(e) => { e.stopPropagation(); remove(a, e.target); }}>remove</button>
        </div>
        {open === a.paradigm_id ? <RunList a={a} /> : null}
      </>
    );
  };

  // Where plugin schedules are changed now. The plugins page links each plugin
  // to its own setup page on Paradigm, for the acting group.
  const toPlugins = (e) => {
    if (!window.navigateTo) return;
    e.preventDefault();
    window.navigateTo('/plugins');
  };

  if (!automations.length) {
    return (
      <>
        <style>{AUTO_STYLE}</style>
        <div className="at-fresh">
          <a className="at-fresh-add" href="/plugins" onClick={toPlugins}>
            plugin schedules are set on Paradigm →
          </a>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{AUTO_STYLE}</style>
      <div className="at-wrap">
        <div className="at-head">
          <div><div className="at-title">automations</div></div>
          <span className="at-note">
            Plugin schedules are set on Paradigm — <a href="/plugins" onClick={toPlugins}>open a plugin to change its schedule</a>
          </span>
        </div>
        {kinds.length ? (
          <div className="at-filters">
            {kinds.map((k) => (
              <button type="button" key={k} className={`at-filter ${filter === k ? 'on' : ''}`} onClick={() => pickFilter(k)}>
                {TAG[k] || k}<span className="at-filter-count">{counts[k]}</span>
              </button>
            ))}
            {filter ? <button type="button" className="at-filter" onClick={() => pickFilter('')}>clear</button> : null}
          </div>
        ) : null}
        {shown.length
          ? <div className="at-list">{shown.map((a) => <Row a={a} key={a.paradigm_id} />)}</div>
          : (
            <div className="at-empty">
              <div className="at-empty-title">nothing matches that</div>
              <div className="at-empty-body">clear the filter to see everything.</div>
            </div>
          )}
      </div>
    </>
  );
}
